import type { ApiPromise } from '@polkadot/api'
import { WalletAccount } from '@talismn/connect-wallets'
import { Badge, Col } from 'react-bootstrap'
import styled from 'styled-components'
import { ClaimPayoutButton } from './ClaimPayoutButton'
import { DataHeaderRow, DataRow } from '../../../../components/base'
import { FormatBalance } from '../../../../components/FormatBalance'
import { useBlockTime } from '../../../../hooks/useBlockTime'
import { useRelayChainBlockNumber } from '../../../../hooks/useRelayChainBlockNumber'
import { Identicon } from '../../components/Identicon'
import { toastByStatus } from '../../helpers'

const StyledDataRow = styled(DataRow)`
  background-color: ${(props) => (props.$isDefender ? props.theme.colors.black : '')};
  border: ${(props) => (props.$isDefender ? `2px solid ${props.theme.colors.secondary}` : '')};

  &:hover {
    cursor: pointer;
  }

  @media (max-width: 992px) {
    padding-block: 12px;
    margin-inline: 2px;
  }
`

type PayoutsListProps = {
  api: ApiPromise
  members: ExtendedSocietyMember[]
  activeAccount: WalletAccount | undefined
  handleUpdate: () => void
}

type TimeRemainingProps = {
  api: ApiPromise
  block: number
  latestBlock: number | null
  member: ExtendedSocietyMember
  activeAccount: WalletAccount | undefined
  handleUpdate: () => void
  'data-test'?: string
} & React.HTMLAttributes<HTMLDivElement>

const CalculatingBadge = () => (
  <Badge pill bg="black" className="me-2 p-2">
    Calculating...
  </Badge>
)

const TimeRemaining = ({
  block,
  latestBlock,
  api,
  member,
  activeAccount,
  handleUpdate,
  'data-test': dataTest
}: TimeRemainingProps) => {
  const isLatestBlockLoading = latestBlock === null
  const blocksLeft = isLatestBlockLoading ? 0 : block - latestBlock
  const [, formattedTime] = useBlockTime(blocksLeft, api, true)

  if (isLatestBlockLoading || !formattedTime) {
    return <CalculatingBadge />
  }

  const isMatured = blocksLeft <= 0

  if (isMatured) {
    const isCurrentUser = activeAccount?.address === member.accountId.toHuman()

    return (
      <>
        <Badge pill bg="primary" className="me-2 p-2">
          Matured
        </Badge>
        {isCurrentUser && (
          <ClaimPayoutButton
            api={api}
            activeAccount={activeAccount}
            showMessage={(result) => toastByStatus[result.status](result.message, { id: result.message })}
            handleUpdate={handleUpdate}
            disabled={false}
            data-test={dataTest}
          />
        )}
      </>
    )
  }

  return (
    <Badge pill bg="secondary" text="black" className="me-2 p-2">
      Maturing in {formattedTime}
    </Badge>
  )
}

const PayoutsList = ({ api, members, activeAccount, handleUpdate }: PayoutsListProps): JSX.Element => {
  const latestBlock = useRelayChainBlockNumber(api)

  if (members.length === 0) return <>No members</>

  return (
    <div data-test="payouts-list">
      <DataHeaderRow className="d-none d-lg-flex text-center">
        <Col lg={1}>#</Col>
        <Col lg={5} className="text-center text-lg-start">
          Wallet Hash
        </Col>
        <Col lg={2} className="text-center text-lg-start">
          Paid
        </Col>
        <Col lg={2} className="text-center text-lg-start">
          Pending
        </Col>
        <Col lg={2}></Col>
      </DataHeaderRow>

      {members.map((member: ExtendedSocietyMember) => (
        <StyledDataRow key={member.accountId.toString()} data-test={`payout-row-${member.accountId.toString()}`}>
          <Col lg={1} className="text-center">
            <Identicon value={member.accountId.toHuman()} size={32} theme={'polkadot'} />
          </Col>
          <Col lg={5} className="text-center text-lg-start">
            {member.accountId.toHuman()}
          </Col>
          <Col
            lg={2}
            className="text-center text-lg-start"
            data-test={`payout-total-${member.accountId.toString()}`}
          >
            <FormatBalance balance={member.extendedPayouts.paid} />
          </Col>
          <Col
            lg={2}
            className="text-center text-lg-start"
            data-test={`payout-pending-${member.accountId.toString()}`}
          >
            <FormatBalance balance={member.extendedPayouts.pending} />
          </Col>
          <Col
            lg={2}
            className="text-center text-lg-end"
            data-test={`payout-maturity-${member.accountId.toString()}`}
          >
            {member.isFounder && (
              <Badge pill bg="dark" className="me-2 p-2">
                Founder
              </Badge>
            )}
            {member.rank.toNumber() > 0 && (
              <Badge pill bg="dark" className="me-2 p-2">
                Ranked
              </Badge>
            )}
            {member.hasPayouts && (
              <TimeRemaining
                block={member.extendedPayouts.block}
                latestBlock={latestBlock}
                api={api}
                member={member}
                activeAccount={activeAccount}
                handleUpdate={handleUpdate}
                data-test={`claim-payout-button-${member.accountId.toString()}`}
              />
            )}
            {member.extendedPayouts.pending == 0 && member.extendedPayouts.paid > 0 && (
              <Badge pill bg="black" className="me-2 p-2">
                Paid
              </Badge>
            )}
            {!member.hasPayouts && member.extendedPayouts.paid == 0 && (
              <Badge pill bg="black" className="me-2 p-2">
                Paid V1
              </Badge>
            )}
          </Col>
        </StyledDataRow>
      ))}
    </div>
  )
}

export { PayoutsList }
